import React from "react";
import NextLink from "next/link";
import Image, { StaticImageData } from "next/image";
import _isEqual from "lodash/isEqual";
import Camera from "assets/icons/camera.svg";
import { LinkProps } from "helpers/url";
import { cn } from "helpers/style";
import { INACTIVE_CARD } from "constants/variables";
import styles from "./LazyImage.module.scss";

declare type SafeNumber = number | `${number}`;

export type Props = {
  src: string | StaticImageData;
  alt?: string;
  title?: string;
  linkProps?: LinkProps;
  onClick?: () => void;
  background?: string;
  ariaLabel?: string;
  priority?: boolean;
  width?: SafeNumber;
  height?: SafeNumber;
  onMouseDown?: (event: React.MouseEvent) => void;
  className?: string;
  classNameNoImg?: string;
};

const LazyImage: React.FC<Props> = ({
  src,
  alt,
  title,
  linkProps,
  width,
  height,
  onClick,
  background,
  priority,
  ariaLabel,
  onMouseDown,
  className,
  classNameNoImg,
}: Props) => {
  const ImageComponent = () => {
    return width || height ? (
      <Image
        onClick={() => onClick && onClick()}
        onMouseDown={onMouseDown}
        src={src}
        alt={alt || ariaLabel}
        title={title || ariaLabel}
        width={width}
        height={height}
        priority={priority}
        unoptimized
      />
    ) : (
      <Image
        onClick={() => onClick && onClick()}
        onMouseDown={onMouseDown}
        src={src}
        alt={alt || ariaLabel}
        title={title || ariaLabel}
        priority={priority}
        unoptimized
        fill
      />
    );
  };

  return linkProps ? (
    <NextLink
      {...linkProps}
      className={cn(["lazy-image", styles.lazyImageContainer, className])}
      aria-label={ariaLabel}
      passHref
    >
      {src ? (
        <ImageComponent />
      ) : (
        <div
          className={cn([styles.lazyImagePlaceholder, classNameNoImg])}
          style={{ background: background ? background : INACTIVE_CARD }}
        >
          <Camera />
          <span className={styles.lazyImagePlaceholderText}>
            {alt || "Изображение отсутствует"}
          </span>
        </div>
      )}
    </NextLink>
  ) : (
    <div className={cn(["lazy-image", styles.lazyImageContainer, className])}>
      {src ? (
        <ImageComponent />
      ) : (
        <div
          className={cn([styles.lazyImagePlaceholder, classNameNoImg])}
          style={{ background: background ? background : "inherit" }}
        >
          <Camera />
          <span className={styles.lazyImagePlaceholderText}>
            {alt || "Изображение отсутствует"}
          </span>
        </div>
      )}
    </div>
  );
};

export default React.memo(LazyImage, (prevProps, nextProps) => {
  /* deep compare, linkProps maybe different object */
  return _isEqual(prevProps, nextProps);
});
